import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { useOfficeMailboxes } from '../../utils/officeMailboxes';

// ── Late-filing Penalty & Surcharge (Nick's policy doc, owner approved 2026-09-12) ──────────────
// The amount is NEVER computed: rules shift, and a misfigured penalty is worse than none. The
// operator files late on the official RD / SSO system, reads the charge THERE, and types it here.
// The card then shows Total = tax + penalty, and offers Nick's "Tax Payment Notification (Late)"
// email (TPL-051) — previewed by a human, sent by a human, never automatic. Anyone may enter the
// figure (owner ruling 1: it is a transcription, not a judgement); the row records who and when.

export default function PenaltyCard({ clientId, kind, year, month, filingNo = 0 }: {
  clientId: string; kind: string; year: number; month: number; filingNo?: number;
}) {
  const { t } = useTranslation();
  const [st, setSt] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState<any>(null);      // preview modal state
  const [to, setTo] = useState('');
  // Default CC = the accounting firm's office mailbox (Admin → Companies) until the operator types one.
  const officeCc = useOfficeMailboxes().accounting;
  const [ccTyped, setCc] = useState<string | null>(null);
  const cc = ccTyped ?? officeCc;
  const [subject, setSubject] = useState('');
  const bodyRef = useRef<HTMLDivElement | null>(null);  // operator-editable body (owner 2026-09-13)
  const [sent, setSent] = useState('');
  // "Request payment – K. Nacha" (owner 2026-09-13): the SAME payment-request pipeline the Filing
  // Center uses — one client, this kind/period. The body is rebuilt server-side on send, so this
  // card can never state a different figure than the Filing Center list.
  const [pay, setPay] = useState<any>(null);
  const [payTo, setPayTo] = useState('');
  const [payCc, setPayCc] = useState('');

  const load = async () => {
    try {
      const d: any = await api.taxPenaltyGet(clientId, kind, year, month, filingNo);
      setSt(d); setAmount(d.penalty ? String(d.penalty) : ''); setNote(d.note || '');
    } catch { /* leave empty */ }
  };
  useEffect(() => { setSent(''); void load(); }, [clientId, kind, year, month, filingNo]); // eslint-disable-line

  const save = async () => {
    setBusy(true); setErr('');
    try { await api.taxPenaltySet({ clientId, kind, year, month, filing: filingNo, amount: Number(amount) || 0, note }); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const preview = async () => {
    setBusy(true); setErr('');
    try { const d: any = await api.taxLateNoticePreview({ clientId, kind, year, month, filing: filingNo }); setNotice(d); setTo(d.to || ''); setSubject(d.subject || ''); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true); setErr('');
    try { const d: any = await api.taxLateNoticeSend({ clientId, kind, year, month, filing: filingNo, to, cc, subject, bodyHtml: bodyRef.current?.innerHTML || '' }); setSent(d.sent); setNotice(null); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const payPreview = async () => {
    setBusy(true); setErr('');
    try {
      const d: any = await api.payReqPreview({ kind, year, month, clientIds: [clientId] });
      setPay(d); setPayTo(Array.isArray(d.to) ? d.to.join(', ') : String(d.to || '')); setPayCc(Array.isArray(d.cc) ? d.cc.join(', ') : String(d.cc || ''));
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const paySend = async () => {
    setBusy(true); setErr('');
    try { const d: any = await api.payReqSend({ kind, year, month, clientIds: [clientId], to: payTo, cc: payCc }); setSent(d.sent); setPay(null); }
    catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!st) return null;
  const penalty = Number(amount) || 0;
  const total = (Number(st.taxAmount) || 0) + penalty;
  const inp: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' };
  return (
    <div style={{ margin: '10px 0', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', background: penalty > 0 ? '#FFFBEB' : 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 800 }}><i className="ti ti-alert-octagon" style={{ marginRight: 5, color: '#B45309' }} />{t('pen_title')}</span>
        <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('pen_hint')}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <input value={amount} onChange={e => setAmount(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0.00" inputMode="decimal"
          style={{ ...inp, width: 110, textAlign: 'right', fontWeight: 700 }} />
        <input value={note} onChange={e => setNote(e.target.value)} placeholder={t('pen_note_ph')} style={{ ...inp, flex: '1 1 200px' }} />
        <button onClick={save} disabled={busy} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#0E7C66', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t('save')}</button>
        {penalty > 0 && (
          <button onClick={preview} disabled={busy} title={t('pen_email_hint')}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #B45309', background: 'transparent', color: '#B45309', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            <i className="ti ti-mail" style={{ marginRight: 4 }} />{t('pen_email_btn')}
          </button>
        )}
        {total > 0 && Number(st.penalty) > 0 && (
          <button onClick={payPreview} disabled={busy} title={t('pen_pay_hint')}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #0E7C66', background: 'transparent', color: '#0E7C66', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            <i className="ti ti-cash-banknote" style={{ marginRight: 4 }} />{t('pen_pay_btn')}
          </button>
        )}
      </div>
      <div style={{ marginTop: 7, fontSize: 12 }}>
        {t('pen_total_line', { tax: money(st.taxAmount), pen: money(penalty) })} <b style={{ color: '#B45309' }}>THB {money(total)}</b>
        {st.byName && Number(st.penalty) > 0 && <span style={{ marginLeft: 10, fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('pen_by', { name: st.byName })}</span>}
        {sent && <span style={{ marginLeft: 10, fontSize: 11, color: '#15803D', fontWeight: 700 }}>{t('pen_sent', { to: sent })}</span>}
      </div>
      {err && <div style={{ marginTop: 6, fontSize: 11.5, color: '#B42318', fontWeight: 600 }}>{err}</div>}

      {pay && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 11500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '5vh 16px' }}
          onMouseDown={e => e.target === e.currentTarget && setPay(null)}>
          <div style={{ width: 660, maxWidth: '96vw', background: 'var(--bg-surface)', borderRadius: 14, padding: 20, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>{t('pen_pay_btn')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, width: 56 }}>To:</span>
              <input value={payTo} onChange={e => setPayTo(e.target.value)} style={{ ...inp, flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, width: 56 }}>Cc:</span>
              <input value={payCc} onChange={e => setPayCc(e.target.value)} style={{ ...inp, flex: 1 }} />
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>{pay.subject}</div>
            {/* Read-only: the payment request is rebuilt server-side on send (single money source). */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, maxHeight: '48vh', overflowY: 'auto', background: '#fff', color: '#111' }}
              dangerouslySetInnerHTML={{ __html: pay.bodyHtml }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
              <button onClick={() => setPay(null)} style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
              <button onClick={paySend} disabled={busy || !payTo.trim()} style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: '#0E7C66', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                {busy ? '…' : t('pen_send_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 11500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '5vh 16px' }}
          onMouseDown={e => e.target === e.currentTarget && setNotice(null)}>
          <div style={{ width: 660, maxWidth: '96vw', background: 'var(--bg-surface)', borderRadius: 14, padding: 20, boxShadow: 'var(--shadow-md)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>{t('pen_modal_title')}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, width: 56 }}>To:</span>
              <input value={to} onChange={e => setTo(e.target.value)} style={{ ...inp, flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, width: 56 }}>Cc:</span>
              <input value={cc} onChange={e => setCc(e.target.value)} style={{ ...inp, flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, width: 56 }}>{t('et_subject')}:</span>
              <input value={subject} onChange={e => setSubject(e.target.value)} style={{ ...inp, flex: 1 }} />
            </div>
            {/* Server-rendered draft the operator may edit in place before sending (owner 2026-09-13);
                what is sent is exactly what this box holds when Send is clicked. */}
            <div ref={bodyRef} contentEditable suppressContentEditableWarning
              style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, maxHeight: '48vh', overflowY: 'auto', background: '#fff', color: '#111', outline: 'none' }}
              dangerouslySetInnerHTML={{ __html: notice.bodyHtml }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
              <button onClick={() => setNotice(null)} style={{ padding: '8px 16px', borderRadius: 9, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
              <button onClick={send} disabled={busy || !to.trim()} style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: '#B45309', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                {busy ? '…' : t('pen_send_btn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
